import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * Switches between bundle sibling products via soft navigation (morph).
 * Dispatches `bundle-sibling:changed` so dependent blocks (bulk qty, included
 * items) re-sync variant/product state without a full page reload.
 *
 * @extends {Component}
 */
class BundleSiblingSwitcher extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#onPopState);
    this.#abortController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('[data-bundle-sibling-url]');
    if (!(option instanceof HTMLElement)) return;
    if (option.classList.contains('is-selected') || option.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const productUrl = option.dataset.bundleSiblingUrl;
    if (!productUrl) return;

    event.preventDefault();

    // Full page navigation: the Shopify Bundles app does not reinitialize
    // after a morph, so soft navigation is not safe for bundle products.
    // window.location.assign(productUrl);

    this.#loadProduct(productUrl);
  };

  #onPopState = () => {
    if (this.dataset.templateProductMatch !== 'true') return;
    this.#loadProduct(window.location.pathname, { updateHistory: false });
  };

  /**
   * @param {string} productUrl
   * @param {{ updateHistory?: boolean }} [options]
   */
  async #loadProduct(productUrl, options = {}) {
    const { updateHistory = true } = options;
    const requestUrl = this.#buildRequestUrl(productUrl);

    if (requestUrl === this.dataset.pendingUrl && this.#isLoading) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#setLoading(true);
    this.dataset.pendingUrl = requestUrl;

    try {
      const response = await fetch(requestUrl, { signal: this.#abortController.signal });

      if (!response.ok) {
        throw new Error(`Failed to load product (${response.status})`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      this.#updateMain(html);
      this.#updateDocumentMeta(html);
      this.#syncLazySections(html);
      this.#notifySectionsLoaded();

      const newSwitcher = /** @type {HTMLElement | null} */ (
        html.querySelector('bundle-sibling-switcher')
      );
      const newProductId = newSwitcher?.dataset?.productId;
      const newProductUrl = newSwitcher?.dataset?.productUrl;

      if (newProductId) this.dataset.productId = newProductId;
      if (newProductUrl) this.dataset.productUrl = newProductUrl;

      if (updateHistory) {
        this.#updateHistory(productUrl);
      }

      this.dispatchEvent(
        new CustomEvent('bundle-sibling:changed', {
          bubbles: true,
          detail: {
            productId: newProductId,
            productUrl: newProductUrl || productUrl,
          },
        })
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('[BundleSiblingSwitcher]', error);
      window.location.assign(productUrl);
    } finally {
      this.dataset.pendingUrl = '';
      this.#setLoading(false);
    }
  }

  /**
   * @param {string} productUrl
   * @returns {string}
   */
  #buildRequestUrl(productUrl) {
    const url = productUrl.startsWith('http')
      ? new URL(productUrl)
      : new URL(productUrl, window.location.origin);

    return url.pathname;
  }

  /**
   * @param {Document} newHtml
   */
  #updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No main element found for bundle sibling morph');
    }

    morph(main, newMain);
  }

  /**
   * @param {Document} newHtml
   */
  #updateDocumentMeta(newHtml) {
    const newTitle = newHtml.querySelector('title')?.textContent?.trim();

    if (newTitle) {
      document.title = newTitle;
    }
  }

  /**
   * Replaces lazy-loaded sections so they re-init for the switched product.
   * @param {Document} newHtml
   */
  #syncLazySections(newHtml) {
    newHtml.querySelectorAll('product-recommendations').forEach((newEl) => {
      if (!(newEl instanceof HTMLElement)) return;

      const sectionId = newEl.dataset.sectionId;
      const selector = sectionId
        ? `product-recommendations[data-section-id="${CSS.escape(sectionId)}"]`
        : 'product-recommendations';
      const currentEl = document.querySelector(selector);

      if (!(currentEl instanceof HTMLElement) || !currentEl.parentNode) return;

      const replacement = /** @type {HTMLElement} */ (newEl.cloneNode(true));
      currentEl.replaceWith(replacement);
    });
  }

  #notifySectionsLoaded() {
    document.querySelectorAll('main .shopify-section').forEach((section) => {
      if (!(section instanceof HTMLElement) || !section.id) return;

      const sectionId = section.id.replace(/^shopify-section-/, '');
      section.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId },
        })
      );
    });
  }

  /**
   * @param {string} productUrl
   */
  #updateHistory(productUrl) {
    const url = new URL(window.location.href);
    const path = productUrl.startsWith('http')
      ? new URL(productUrl).pathname
      : (productUrl.split('?')[0] || productUrl);

    url.pathname = path;
    url.searchParams.delete('variant');

    if (url.href === window.location.href) return;

    requestYieldCallback(() => {
      history.replaceState({ bundleSibling: true }, '', url.toString());
    });
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.#isLoading = loading;
    this.classList.toggle('is-loading', loading);
    this.toggleAttribute('aria-busy', loading);

    this.querySelectorAll('[data-bundle-sibling-url]').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      if (option.classList.contains('is-selected')) return;
      option.setAttribute('aria-disabled', loading ? 'true' : 'false');
    });
  }
}

if (!customElements.get('bundle-sibling-switcher')) {
  customElements.define('bundle-sibling-switcher', BundleSiblingSwitcher);
}